import { useLayoutEffect, useRef, type Ref } from "react";
import { Link, useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import {
  SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu,
  SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, useSidebar,
} from "@/components/ui/sidebar";
import { useSessionWorkspace } from "@/hooks/use-session-workspace";
import { ApiError } from "@/lib/api/client";
import { COURSE_HUE, courseSignature } from "@/lib/course-signature";
import type { NavGroup } from "@/navigation/sidebar-items";
import { courseSectionTag, type CourseSectionIdentity } from "@/lib/course-section-tag";

import { RAIL_ITEM, RAIL_LIFT } from "./rail-item";

interface NavigationCourse extends CourseSectionIdentity {
  course_id: string;
  code: string;
  title: string;
  crn: string | null;
  href: string | null;
  term: { name: string; status: "active" | "upcoming" | "closed" } | null;
}

interface CourseNavigationQuery {
  data?: { shell_base: string; courses: NavigationCourse[] };
  error: unknown;
  isPending: boolean;
  isError: boolean;
  isNoSeat: boolean;
  refetch: () => unknown;
}

/** A course is selected on its own workspace and on the portal of one of its
 * sessions. Query text never selects a course. */
function isCourseActive(course: NavigationCourse, pathname: string, sessionCourseId?: string): boolean {
  return pathname === course.href || (sessionCourseId !== undefined && sessionCourseId === course.course_id);
}

function CourseNavigationItem({ course, active, sharedTerm, stale, selectedRef, sectionTag, closeMobile }: {
  course: NavigationCourse & { href: string };
  active: boolean;
  sharedTerm: string | null;
  stale: boolean;
  selectedRef?: Ref<HTMLAnchorElement>;
  sectionTag: string | null;
  closeMobile: () => void;
}) {
  const secondary = [course.code, course.term?.name, course.crn ? `CRN ${course.crn}` : null].filter(Boolean).join(" · ");
  const identity = [course.title, sectionTag].filter(Boolean).join(" · ");
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild size="lg" isActive={active} tooltip={`${identity} · ${secondary}${stale ? " · Saved courses; could not refresh" : ""}`} className={`${RAIL_LIFT} h-auto min-h-[var(--control-height-large)] min-w-0 flex-1 items-start py-2`}>
        <Link ref={selectedRef} to={course.href} onClick={closeMobile} aria-label={`${identity}, ${secondary}`} aria-current={active ? "true" : undefined}>
          <span aria-hidden="true" data-course-hue={courseSignature(course.code).hue} className="mt-[0.4375rem] size-2 shrink-0 rounded-full" style={{ backgroundColor: COURSE_HUE[courseSignature(course.code).hue] }} />
          <span className="min-w-0 group-data-[collapsible=icon]:hidden">
            <span className="block truncate font-mono text-key font-normal text-muted-foreground" title={secondary}>{sharedTerm || !course.term ? course.code : `${course.code} · ${course.term.name}`}</span>
            <span className={`block truncate text-ui ${active ? "font-semibold" : "font-normal"}`} title={course.title}>{course.title}</span>
            {sectionTag && <span className="block truncate text-meta text-muted-foreground" title={sectionTag}>{sectionTag}</span>}
          </span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function CourseSeatNavigation({ group, query, capacityLabel, hideNoSeat }: {
  group: NavGroup;
  query: CourseNavigationQuery;
  capacityLabel: "Teaching" | "Learning";
  hideNoSeat: boolean;
}) {
  const location = useLocation();
  const { isMobile, setOpenMobile, openMobile, state } = useSidebar();
  const selectedCourseRef = useRef<HTMLAnchorElement>(null);
  // Seat destinations precede the course collection; Home supplies its base.
  const [home] = group.items;
  const sessionMatch = location.pathname.match(new RegExp(`^${home.url}/sessions/([^/]+)$`));
  const session = useSessionWorkspace(sessionMatch?.[1]);
  const sessionRefused = session.error instanceof ApiError
    && [401, 403, 404].includes(session.error.status);
  const sessionCourseId = sessionMatch && !sessionRefused ? session.data?.session.course_id : undefined;
  const refused = query.error instanceof ApiError && [401, 403, 404].includes(query.error.status);
  const data = refused || query.data?.shell_base !== home.url ? undefined : query.data;
  const linkedCourses = data?.courses.filter((course): course is NavigationCourse & { href: string } => course.href !== null) ?? [];
  const current = linkedCourses.filter((course) => course.term?.status === "active");
  const courses = current.length > 0
    ? current
    : linkedCourses.filter((course) => course.term?.status === "upcoming");
  // One term names the list, as the console's term label does; a list that
  // spans terms names each row's term instead.
  const termNames = new Set(courses.map((course) => course.term?.name ?? null));
  const sharedTerm = termNames.size === 1 ? [...termNames][0] : null;
  const label = current.length === 0 && courses.length > 0
    ? "Upcoming courses"
    : sharedTerm ?? "Current courses";
  useLayoutEffect(() => {
    const selected = selectedCourseRef.current;
    const scrollArea = selected?.closest<HTMLElement>('[data-sidebar="content"]');
    if (!selected || !scrollArea) return;
    const link = selected.getBoundingClientRect();
    const viewport = scrollArea.getBoundingClientRect();
    // A registrar's earlier groups can push the teaching course below the
    // footer. Reveal only that item within the sidebar, never scroll the page.
    const gutter = 8;
    if (link.bottom > viewport.bottom - gutter) {
      scrollArea.scrollTop += Math.ceil(link.bottom - viewport.bottom + gutter);
    } else if (link.top < viewport.top + gutter) {
      scrollArea.scrollTop -= Math.ceil(viewport.top + gutter - link.top);
    }
  }, [location.pathname, location.search, sessionCourseId, data, openMobile, state]);
  const closeMobile = () => { if (isMobile) setOpenMobile(false); };
  const standardLink = (item: typeof home) => (
    <SidebarMenuItem key={item.url}>
      <SidebarMenuButton asChild tooltip={item.title} isActive={location.pathname === item.url} className={RAIL_ITEM}>
        <Link to={item.url} onClick={closeMobile} aria-current={location.pathname === item.url ? "page" : undefined}>
          {item.icon && <item.icon />}
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  // A live no-seat answer outranks stale /me capacity. Other seats stay usable.
  if (hideNoSeat && query.isNoSeat) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="eyebrow">{group.label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {group.items.map(standardLink)}
          <SidebarMenuItem className="mt-3 group-data-[collapsible=icon]:hidden">
            <p className="eyebrow px-2 pb-1">{label}</p>
            {query.isPending && !data ? (
              <div role="status" className="grid gap-1 py-1">
                <span className="sr-only">Loading courses...</span>
                <SidebarMenuSkeleton showIcon />
                <SidebarMenuSkeleton showIcon />
              </div>
            ) : null}
            {!data && query.isError ? (
              <div role="alert" className="grid justify-items-start gap-1 px-2 py-2 text-meta text-foreground-soft">
                <p>{refused ? `${capacityLabel} courses unavailable.` : "Could not load courses."}</p>
                <Button type="button" variant="link" size="sm" className="px-0" onClick={() => void query.refetch()}>Try again</Button>
              </div>
            ) : null}
            {data && query.isError ? <p role="status" className="px-2 py-2 text-meta text-foreground-soft">Could not refresh. Showing saved courses.</p> : null}
            {data && courses.length === 0 ? <p className="px-2 py-2 text-meta text-muted-foreground">No current or upcoming courses.</p> : null}
          </SidebarMenuItem>
          {courses.map((course) => {
            const active = isCourseActive(course, location.pathname, sessionCourseId);
            return (
              <CourseNavigationItem key={course.course_id} course={course} active={active} sharedTerm={sharedTerm} stale={query.isError} sectionTag={courseSectionTag(course.course_id, courses)}
                selectedRef={active ? selectedCourseRef : undefined} closeMobile={closeMobile} />
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
